import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Download } from "lucide-react";
import { exportProjectsJson, exportProjectsCsv } from "../lib/export";
import { loadRevenueGoal, saveRevenueGoal, DEFAULT_REVENUE_GOAL, fmtEUR } from "../lib/finance";
import SpecialDaysSection from "../components/SpecialDaysSection";

const TOKEN_KEY = "wallplanner_token";

function formatErr(detail) {
  if (!detail) return "Aktion fehlgeschlagen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState(user?.email || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(null); // null | "json" | "csv"
  const [revenueGoal, setRevenueGoal] = useState(() => String(loadRevenueGoal()));

  const handleSaveRevenueGoal = () => {
    const n = Number(revenueGoal);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Bitte eine gültige Zahl (>= 0) eingeben.");
      setRevenueGoal(String(loadRevenueGoal()));
      return;
    }
    saveRevenueGoal(n);
    setRevenueGoal(String(n));
    toast.success(`Jahresinkasso-Ziel gespeichert (${fmtEUR(n)})`);
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const { data } = await axios.get(`${API}/projects`);
      const projects = Array.isArray(data) ? data : [];
      if (format === "json") exportProjectsJson(projects);
      else exportProjectsCsv(projects);
      toast.success(
        `Backup heruntergeladen (${projects.length} Projekt${projects.length === 1 ? "" : "e"})`,
      );
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message || "Export fehlgeschlagen.");
    } finally {
      setExporting(null);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Bitte aktuelles Passwort eingeben.");
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein.");
      return;
    }
    if (newPassword && newPassword.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen haben.");
      return;
    }

    const payload = { current_password: currentPassword };
    if (newEmail && newEmail.toLowerCase() !== (user?.email || "").toLowerCase()) {
      payload.new_email = newEmail;
    }
    if (newPassword) payload.new_password = newPassword;

    if (!payload.new_email && !payload.new_password) {
      toast.info("Keine Änderungen.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.put(`${API}/auth/me`, payload);
      localStorage.setItem(TOKEN_KEY, data.token);
      refreshUser({ id: data.id, email: data.email, name: data.name });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Konto aktualisiert");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-start justify-center py-16 px-4" data-testid="settings-page">
      <div className="w-full max-w-[680px]">
        <button
          onClick={() => navigate("/")}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-6"
          data-testid="settings-back-button"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Zurück zum Planer
        </button>

        <div className="bg-white border border-slate-200 rounded-md p-8 mb-4" data-testid="settings-export-card">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">
            Daten-Backup
          </div>
          <div className="text-sm text-slate-700 mb-1">Alle Projekte exportieren</div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
            Lade eine Kopie deiner Projekte herunter — als JSON für vollständige Sicherung
            oder als CSV für Tabellenkalkulation. Es wird nichts hochgeladen oder geändert.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleExport("json")}
              disabled={exporting !== null}
              className="h-9 gap-1.5"
              data-testid="settings-export-json-button"
            >
              <Download className="w-3.5 h-3.5" />
              {exporting === "json" ? "Wird exportiert…" : "JSON-Backup"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleExport("csv")}
              disabled={exporting !== null}
              className="h-9 gap-1.5"
              data-testid="settings-export-csv-button"
            >
              <Download className="w-3.5 h-3.5" />
              {exporting === "csv" ? "Wird exportiert…" : "CSV-Tabelle"}
            </Button>
          </div>
        </div>

        <SpecialDaysSection />

        <div className="bg-white border border-slate-200 rounded-md p-8 mb-4" data-testid="settings-revenue-goal-card">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">
            Finanzen
          </div>
          <div className="text-sm text-slate-700 mb-1">Jahresinkasso-Ziel</div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
            Zielwert für das Jahresinkasso in Euro. Wird in der Finanzansicht nur zur
            Farbgebung des Jahresinkasso-Werts genutzt (rot unter Ziel, grün ab Ziel).
            Standardwert: {fmtEUR(DEFAULT_REVENUE_GOAL)}.
          </p>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              step={1000}
              value={revenueGoal}
              onChange={(e) => setRevenueGoal(e.target.value)}
              onBlur={handleSaveRevenueGoal}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveRevenueGoal(); } }}
              className="h-9 w-[180px] tabular-nums"
              data-testid="settings-revenue-goal-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleSaveRevenueGoal}
              className="h-9"
              data-testid="settings-revenue-goal-save"
            >
              Speichern
            </Button>
            <span className="text-[11px] text-slate-400 ml-1">EUR</span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-8">
          <div className="mb-7">
            <div className="text-[11px] tracking-[0.2em] uppercase text-slate-500 mb-2">
              Konto
            </div>
            <h1
              className="text-3xl tracking-tight font-semibold text-slate-900"
              style={{ fontFamily: "Manrope, ui-sans-serif" }}
            >
              Einstellungen
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              E-Mail und Passwort des Owner-Kontos ändern.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Aktuelles Passwort <span className="text-rose-500">*</span>
              </Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="h-10 mt-1.5"
                data-testid="settings-current-password-input"
                autoComplete="current-password"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Erforderlich, um Änderungen zu bestätigen.
              </p>
            </div>

            <div className="border-t border-slate-100 pt-5">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">
                E-Mail ändern
              </div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Neue E-Mail
              </Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="h-10 mt-1.5"
                data-testid="settings-new-email-input"
                autoComplete="email"
              />
            </div>

            <div className="border-t border-slate-100 pt-5">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">
                Passwort ändern (optional)
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    Neues Passwort
                  </Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="h-10 mt-1.5"
                    minLength={8}
                    data-testid="settings-new-password-input"
                    autoComplete="new-password"
                    placeholder="Mind. 8 Zeichen"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    Passwort bestätigen
                  </Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-10 mt-1.5"
                    data-testid="settings-confirm-password-input"
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/")}
                className="h-10"
                data-testid="settings-cancel-button"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="h-10 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="settings-save-button"
              >
                {submitting ? "Wird gespeichert…" : "Speichern"}
              </Button>
            </div>
          </form>
        </div>

        <div className="text-[10px] text-slate-400 mt-4 px-2">
          Angemeldet als <Link to="/" className="underline">{user?.email}</Link>
        </div>
      </div>
    </div>
  );
}
